'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { NextIntlClientProvider, useTranslations } from 'next-intl';
import { getInterviewMessages } from '@/i18n/interviewLocale';
import type { InterviewLanguageConfig } from '@/types/pulse';
import {
  RetryButton,
  RouteErrorFallback,
  useCaptureRouteError
} from '@/components/ui/RouteErrorFallback';

function ErrorContent({ reset }: { reset: () => void }) {
  const t = useTranslations('error');
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <RouteErrorFallback title={t('loadTitle')} description={t('loadBody')}>
        <RetryButton onClick={reset}>{t('refresh')}</RetryButton>
      </RouteErrorFallback>
    </div>
  );
}

export default function InterviewRuntimeError({
  error,
  reset
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const pathname = usePathname();
  const [languageConfig, setLanguageConfig] =
    useState<InterviewLanguageConfig | null>(null);

  useCaptureRouteError(error);

  useEffect(() => {
    const slug = pathname?.match(/^\/i\/([^/]+)/)?.[1];
    if (!slug) return;

    let cancelled = false;
    fetch(`/api/interview-locale/${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.languageConfig) {
          setLanguageConfig(data.languageConfig);
        }
      })
      .catch(() => {
        // keep English default
      });

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  const { locale, messages } = getInterviewMessages(languageConfig);

  return (
    <NextIntlClientProvider locale={locale} messages={messages}>
      <ErrorContent reset={reset} />
    </NextIntlClientProvider>
  );
}
